// VitalsGraphs.tsx
import React, { memo } from 'react';
import GraphLinear from '../../container-for-graphics/components/graph-linear/graph-linear';
import { OxygenSaturationDonuts } from './../graphs/donut-chart';
import EmptyState from './../graphs/empty-grphs';
import { TFunction } from 'i18next';

type VitalsProps = {
  heartRate: { dataHR; optionsFC };
  oxygenSaturation: { min: number; max: number } | null;
  t: TFunction<'translation'>;
  setLoading: (v: boolean) => void;
};

function VitalsGraphsImpl({ heartRate, oxygenSaturation, t }: VitalsProps) {
  const hasHR = !!heartRate?.dataHR?.labels?.length;
  const hasO2 = !!oxygenSaturation;

  if (!hasHR && !hasO2) {
    return <EmptyState title={t('emptyMessage')} icon="pi pi-heart" />;
  }

  // ¿Tenemos valores numéricos y una max > 0?
  const hasValidO2Data =
    !!oxygenSaturation &&
    typeof oxygenSaturation.min === 'number' &&
    typeof oxygenSaturation.max === 'number' &&
    oxygenSaturation.max > 0;

  // Lógica de min a mostrar:
  // - Si min === 0 -> usamos max para que no salga 0
  // - Si min !== 0 -> usamos min real
  const o2MinToDisplay = hasValidO2Data
    ? oxygenSaturation!.min === 0
      ? oxygenSaturation!.max
      : oxygenSaturation!.min
    : undefined;

  const o2MaxToDisplay = hasValidO2Data ? oxygenSaturation!.max : undefined;

  return (
    <div className="w-full block">
      {/* FC */}
      {hasHR && (
        <>
          <div className="text-2xl font-bold pl-2 mt-3">
            {t('pages.patients.viewPatient.sessions.fc')}
          </div>
          <div className="w-full">
            <div className="h-24rem md:h-26rem lg:h-30rem overflow-hidden">
              <GraphLinear
                data={heartRate.dataHR}
                // Fuerza animation a false, respetando el resto de opciones
                options={{ ...(heartRate.optionsFC ?? {}), animation: false }}
              />
            </div>
          </div>
        </>
      )}

      {/* O2 */}
      {hasO2 ? (
        <>
          <div className="w-full text-2xl font-bold my-3 text-center">
            {t('pages.patients.viewPatient.sessions.so2')}
          </div>

          {hasValidO2Data &&
          o2MinToDisplay !== undefined &&
          o2MaxToDisplay !== undefined ? (
            <div className="grid grid-cols-12 sm:grid-cols-1 justify-content-center align-items-center mt-4">
              <OxygenSaturationDonuts
                min={o2MinToDisplay}
                max={o2MaxToDisplay}
              />
            </div>
          ) : (
            // No hay datos útiles de O2
            <div className="w-full text-center my-4">
              {t('pages.patients.viewPatient.sessions.so2NoData')}
            </div>
          )}
        </>
      ) : (
        // No hay medición de O2
        <div className="w-full text-center my-4">
          {t('pages.patients.viewPatient.sessions.so2NoData')}
        </div>
      )}
    </div>
  );
}

// memo para evitar re-render si las props no cambian
const VitalsGraphs = memo(VitalsGraphsImpl);
export default VitalsGraphs;
